import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Sparkles, Loader2, Download, Trash2, Info, Type, Image as ImageIcon, Upload, Footprints, Link2, Gamepad2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StylePicker } from "@/components/StylePicker";
import { DEFAULT_STYLE } from "@/lib/styles";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PENDING = ["queued", "processing"];
const STAGE_LABEL = { queued: "Queued…", preview: "Sculpting shape…", refine: "Texturing (PBR)…", image: "Building from image…", done: "Done" };

// Resize + compress a picked image to a small JPEG data-URI so it posts cleanly to image→3D.
function fileToDataUrl(file, max = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Studio3D() {
  const [mode, setMode] = useState("text");
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [animate, setAnimate] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imgEngine, setImgEngine] = useState("meshy"); // meshy (rigged) | fal (fast static)
  const [rightsOk, setRightsOk] = useState(false);
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState(1);
  const [confirmVariants, setConfirmVariants] = useState(false);
  const [open, setOpen] = useState(null);
  const [games, setGames] = useState([]);
  const [applyOpen, setApplyOpen] = useState(null);
  const [applyGameId, setApplyGameId] = useState("");
  const [applyTarget, setApplyTarget] = useState("player");
  const [applying, setApplying] = useState(false);
  const [castOpen, setCastOpen] = useState(false);
  const [castGameId, setCastGameId] = useState("");
  const [castRoles, setCastRoles] = useState({});
  const [applyingCast, setApplyingCast] = useState(false);
  const timer = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/meshy/models"); setModels(data); return data; } catch { return []; }
  }, []);

  useEffect(() => {
    load();
    api.get("/games").then((r) => setGames((r.data || []).filter((g) => g.config?.dimension === "3d"))).catch(() => {});
    timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [load]);

  const openApply = (m) => { setApplyOpen(m); setApplyGameId(games[0]?.id || ""); setApplyTarget("player"); };
  const doApply = async () => {
    if (!applyGameId) { toast.error("Pick a game first."); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/meshy/models/${applyOpen.id}/apply`, { game_id: applyGameId, target: applyTarget });
      toast.success(`Added "${applyOpen.name}" as ${applyTarget}${data.animated ? " — animated! 🎬" : ""} 🎮`);
      setApplyOpen(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const readyModels = models.filter((m) => m.status === "succeeded");
  const openCast = () => { setCastGameId(games[0]?.id || ""); setCastRoles({}); setCastOpen(true); };
  const setRole = (id, target) => setCastRoles((r) => ({ ...r, [id]: target }));
  const doApplyCast = async () => {
    const assignments = Object.entries(castRoles).filter(([, t]) => t && t !== "skip").map(([model_id, target]) => ({ model_id, target }));
    if (!castGameId) { toast.error("Pick a game first."); return; }
    if (assignments.length === 0) { toast.error("Assign a role to at least one character."); return; }
    setApplyingCast(true);
    try {
      const { data } = await api.post("/meshy/apply-cast", { game_id: castGameId, assignments });
      toast.success(`Added a cast of ${data.count} character${data.count > 1 ? "s" : ""} to your game! 🎬`);
      setCastOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplyingCast(false); }
  };

  const generateText = async () => {
    if (prompt.trim().length < 2) { toast.error("Describe what to build (e.g. 'a fire dragon warrior')."); return; }
    setBusy(true);
    try {
      if (versions > 1) {
        const { data } = await api.post("/meshy/text-to-3d/variants", { prompt: prompt.trim(), art_style: style, rig: animate, count: versions });
        toast.success(`Sculpting ${versions} versions to pick from — takes a few minutes ✨`);
        setModels((m) => [...(data.models || []), ...m]); setPrompt("");
      } else {
        const { data } = await api.post("/meshy/text-to-3d", { prompt: prompt.trim(), art_style: style, rig: animate });
        toast.success(animate ? "Sculpting + adding animations — takes a few minutes ✨" : "Generating your 3D model — takes a minute or two ✨");
        setModels((m) => [data, ...m]); setPrompt("");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setImageUrl(dataUrl); setImagePreview(dataUrl);
      toast.success("Image ready — pick animations and Generate!");
    } catch { toast.error("Couldn't read that image. Try another one."); }
    e.target.value = "";
  };

  const applyImageUrl = (url) => {
    setImageUrl(url.trim());
    setImagePreview(url.trim());
  };

  const generateImage = async () => {
    if (!imageUrl) { toast.error("Upload an image or paste an image URL first."); return; }
    if (!rightsOk) { toast.error("Please confirm you have the rights to use this image."); return; }
    setBusy(true);
    try {
      if (imgEngine === "fal") {
        const { data } = await api.post("/fal/image-to-3d", { image_url: imageUrl, name: "Image model" });
        toast.success("fal.ai is sculpting a real 3D model from your image — about a minute ⚡");
        setModels((m) => [data, ...m]);
      } else {
        const { data } = await api.post("/meshy/image-to-3d", { image_url: imageUrl, name: "Image character", rig: animate });
        toast.success("Turning your image into 3D — takes a few minutes ✨");
        setModels((m) => [data, ...m]);
      }
      setImageUrl(""); setImagePreview(""); setRightsOk(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/meshy/models/${id}`); setModels((m) => m.filter((x) => x.id !== id)); if (open?.id === id) setOpen(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reanimate = async (m) => {
    try {
      await api.post(`/meshy/models/${m.id}/reanimate`);
      toast.success(`Animating "${m.name}"… this takes about a minute 🎬`);
      setModels((list) => list.map((x) => x.id === m.id ? { ...x, rig_wanted: true, rig_status: "processing" } : x));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start animation");
    }
  };

  const isAnimated = (m) => m.rig_status === "succeeded";
  const viewerSrc = (m) => `${BACKEND}/api/meshy/assets/${m.id}.${isAnimated(m) ? "anim.glb" : "glb"}`;

  const AnimToggle = (
    <button type="button" onClick={() => setAnimate((v) => !v)} data-testid="meshy-animate-toggle"
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${animate ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
      <Footprints className="w-4 h-4" /> {animate ? "With animations" : "Static model"}
    </button>
  );

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="studio-3d-page">
      <PageHeader title="3D Studio" icon={Box} subtitle="Turn a prompt — or any character image — into a real, game-ready, animated 3D model with Meshy AI." />

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6">
        <Tabs value={mode} onValueChange={setMode} className="mb-4">
          <TabsList data-testid="meshy-mode-tabs">
            <TabsTrigger value="text" data-testid="meshy-mode-text"><Type className="w-4 h-4 mr-1" /> From text</TabsTrigger>
            <TabsTrigger value="image" data-testid="meshy-mode-image"><ImageIcon className="w-4 h-4 mr-1" /> From image</TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="mt-4 space-y-4">
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="meshy-prompt"
              placeholder="Describe a single character or object — e.g. 'a fire dragon warrior in heavy armor, A-pose, highly detailed'"
              className="min-h-[84px]" />
            <StylePicker value={style} onChange={setStyle} testid="meshy-style-picker" />
            <div className="flex flex-wrap items-center gap-3">
              {AnimToggle}
              <div className="flex items-center gap-1.5" data-testid="meshy-versions">
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Versions</span>
                {[1, 2, 3].map((n) => (
                  <button key={n} type="button" onClick={() => setVersions(n)} data-testid={`meshy-versions-${n}`}
                    className={`w-7 h-7 rounded-full text-xs font-bold transition-colors ${versions === n ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                    {n}
                  </button>
                ))}
              </div>
              <Button onClick={() => { if (prompt.trim().length < 2) { toast.error("Describe what to build first."); return; } versions > 1 ? setConfirmVariants(true) : generateText(); }} disabled={busy} className="rounded-full ml-auto bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="meshy-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {versions > 1 ? `Generate ${versions} versions` : "Generate 3D model"}
              </Button>
            </div>
            {versions > 1 && <p className="text-[11px] text-[hsl(var(--muted-foreground))] -mt-2">We'll sculpt {versions} different takes so you can pick your favourite. Each version uses 3D credits.</p>}
          </TabsContent>

          <TabsContent value="image" className="mt-4 space-y-4">
            <div className="grid sm:grid-cols-[140px_1fr] gap-4">
              <div className="aspect-square rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))] grid place-items-center overflow-hidden">
                {imagePreview ? <img src={imagePreview} alt="reference" className="w-full h-full object-contain" data-testid="meshy-image-preview" />
                  : <ImageIcon className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />}
              </div>
              <div className="space-y-2.5">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="meshy-image-file" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full w-full sm:w-auto" data-testid="meshy-image-upload-btn">
                  <Upload className="w-4 h-4" /> Upload a character image
                </Button>
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                  <Input placeholder="…or paste an image URL" defaultValue="" onBlur={(e) => e.target.value && applyImageUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && e.target.value && applyImageUrl(e.target.value)} data-testid="meshy-image-url" className="flex-1" />
                </div>
                <label className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))] cursor-pointer">
                  <input type="checkbox" checked={rightsOk} onChange={(e) => setRightsOk(e.target.checked)} className="mt-0.5" data-testid="meshy-rights-check" />
                  <span>I have the rights to use this image and it isn't someone else's copyrighted character.</span>
                </label>
              </div>
            </div>
            <StylePicker value={style} onChange={setStyle} testid="meshy-style-picker-img" label="Style hint (for texture)" />
            <div className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid="img-engine-picker">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">3D engine</div>
              <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-1">
                {[["meshy", "Meshy · rigged", "Walk/run animations for humanoids · a few min"],
                  ["fal", "fal.ai · fast", "Real static mesh from one image · ~1 min"]].map(([k, label, hint]) => (
                  <button key={k} type="button" onClick={() => setImgEngine(k)} title={hint} data-testid={`img-engine-${k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${imgEngine === k ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">
                {imgEngine === "fal"
                  ? "⚡ fal.ai turns your image into a real, game-ready 3D mesh in about a minute (static, no rig)."
                  : "🎬 Meshy builds a textured model and auto-rigs humanoids so they walk/run in-game."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {imgEngine === "meshy" && AnimToggle}
              <Button onClick={generateImage} disabled={busy || !imageUrl} className="rounded-full ml-auto bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="meshy-image-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {imgEngine === "fal" ? "Convert with fal.ai" : "Convert image to 3D"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex items-start gap-1.5 text-xs text-[hsl(var(--muted-foreground))] mt-1 pt-3 border-t border-[hsl(var(--border))]">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Tip: focus on ONE character or object. Humanoid characters get real walk/run animations; other shapes come out as static, game-ready models. Uses your Meshy credits (~1–3 min).</span>
        </div>
      </div>

      {models.length === 0 ? (
        <EmptyState emoji="🧊" title="No 3D models yet" text="Describe a character or drop in an image above and hit Generate — your models appear here." />
      ) : (
        <>
          {readyModels.length >= 2 && games.length > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
              <div className="text-sm"><b>{readyModels.length}</b> finished models · build a whole game cast in one go.</div>
              <Button size="sm" className="rounded-full gap-1.5 bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" onClick={openCast} data-testid="build-cast-btn"><Gamepad2 className="w-4 h-4" /> Build a game cast</Button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="meshy-gallery">
          {models.map((m) => (
            <div key={m.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover flex flex-col" data-testid={`meshy-model-${m.id}`}>
              <div className="aspect-square bg-[hsl(var(--muted))] grid place-items-center relative">
                {m.status === "succeeded" && m.thumbnail_url ? (
                  <img src={m.thumbnail_url} alt={m.name} className="w-full h-full object-cover" />
                ) : m.status === "failed" ? (
                  <div className="text-center p-4"><div className="text-3xl mb-1">⚠️</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{m.error || "Generation failed"}</div></div>
                ) : (
                  <div className="text-center p-4" data-testid={`meshy-progress-${m.id}`}>
                    <Loader2 className="w-7 h-7 animate-spin text-[hsl(var(--primary))] mx-auto mb-2" />
                    <div className="text-xs font-semibold">{STAGE_LABEL[m.stage] || "Working…"}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{m.progress || 0}%</div>
                  </div>
                )}
                {isAnimated(m) && <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white flex items-center gap-0.5"><Footprints className="w-2.5 h-2.5" /> Animated</span>}
                {m.rig_wanted && PENDING.includes(m.rig_status) && m.status === "succeeded" && <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500 text-white">Rigging…</span>}
                {m.variant_group && <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[hsl(var(--primary))] text-white" data-testid={`meshy-variant-badge-${m.id}`}>Version {m.variant_index || "•"}</span>}
              </div>
              <div className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{m.name}</div>
                  <Badge variant={m.status === "succeeded" ? "default" : m.status === "failed" ? "destructive" : "secondary"} className="mt-0.5 text-[10px] capitalize">{m.status}</Badge>
                </div>
                {m.status === "succeeded" && (
                  <>
                    <Button size="sm" className="rounded-full" onClick={() => setOpen(m)} data-testid={`meshy-view-${m.id}`}>View</Button>
                    {m.rig_status !== "succeeded" && !PENDING.includes(m.rig_status) && (
                      <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => reanimate(m)} data-testid={`meshy-reanimate-${m.id}`}><Footprints className="w-3.5 h-3.5" /> Animate</Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => openApply(m)} data-testid={`meshy-addgame-${m.id}`}><Gamepad2 className="w-3.5 h-3.5" /> Add</Button>
                  </>
                )}
                <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`meshy-delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="meshy-viewer-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{open?.name}{open && isAnimated(open) && <Badge className="text-[10px] bg-emerald-500"><Footprints className="w-3 h-3 mr-0.5" /> Animated</Badge>}</DialogTitle><DialogDescription className="sr-only">Interactive 3D model preview and download options.</DialogDescription></DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden bg-[hsl(var(--muted))]" style={{ height: 420 }}>
                {/* eslint-disable-next-line */}
                <model-viewer
                  src={viewerSrc(open)}
                  alt={open.name}
                  camera-controls=""
                  auto-rotate=""
                  autoplay=""
                  shadow-intensity="1"
                  exposure="1.05"
                  style={{ width: "100%", height: "100%", background: "transparent" }}
                  data-testid="model-viewer"
                ></model-viewer>
              </div>
              {isAnimated(open) && <p className="text-xs text-emerald-600 flex items-center gap-1.5"><Footprints className="w-3.5 h-3.5" /> This character walks! The preview is playing its animation.</p>}
              <div className="flex gap-2">
                <a href={`${BACKEND}/api/meshy/assets/${open.id}.glb`} download={`${open.name}.glb`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full" data-testid="meshy-download-glb"><Download className="w-4 h-4" /> Download .glb</Button>
                </a>
                {isAnimated(open) && (
                  <a href={`${BACKEND}/api/meshy/assets/${open.id}.anim.glb`} download={`${open.name}-animated.glb`} className="flex-1">
                    <Button className="w-full rounded-full" data-testid="meshy-download-anim"><Download className="w-4 h-4" /> Download animated .glb</Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!applyOpen} onOpenChange={(o) => !o && setApplyOpen(null)}>
        <DialogContent className="sm:max-w-md" data-testid="meshy-apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[hsl(var(--primary))]" /> Add to a game</DialogTitle>
            <DialogDescription>Drop <b>{applyOpen?.name}</b> straight into one of your 3D games{applyOpen && isAnimated(applyOpen) ? " — it'll walk with its animation." : "."}</DialogDescription>
          </DialogHeader>
          {games.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center" data-testid="apply-no-games">
              You don't have any 3D games yet. Create a 3D game first, then come back to drop this character in.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-xs font-bold mb-1">Game</div>
                <Select value={applyGameId} onValueChange={setApplyGameId}>
                  <SelectTrigger data-testid="apply-game-select"><SelectValue placeholder="Pick a 3D game" /></SelectTrigger>
                  <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-bold mb-1">Role</div>
                <Select value={applyTarget} onValueChange={setApplyTarget}>
                  <SelectTrigger data-testid="apply-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="player">Hero (player)</SelectItem>
                    <SelectItem value="enemy">Enemy</SelectItem>
                    <SelectItem value="boss">Boss</SelectItem>
                    <SelectItem value="npc">NPC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={doApply} disabled={applying || !applyGameId} className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="apply-confirm-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />} Add to game
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={castOpen} onOpenChange={setCastOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="cast-builder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[hsl(var(--primary))]" /> Build a game cast</DialogTitle>
            <DialogDescription>Assign your finished 3D models to roles in one of your 3D games — all at once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-bold mb-1">Game</div>
              <Select value={castGameId} onValueChange={setCastGameId}>
                <SelectTrigger data-testid="cast-game-select"><SelectValue placeholder="Pick a 3D game" /></SelectTrigger>
                <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="cast-model-list">
              {readyModels.map((m) => (
                <div key={m.id} className="flex items-center gap-2" data-testid={`cast-row-${m.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-[hsl(var(--muted))] overflow-hidden grid place-items-center shrink-0">
                    {m.thumbnail_url ? <img src={m.thumbnail_url} alt={m.name} className="w-full h-full object-cover" /> : <Box className="w-4 h-4 opacity-50" />}
                  </div>
                  <span className="flex-1 text-sm font-semibold truncate">{m.name}{isAnimated(m) && <Footprints className="w-3 h-3 inline ml-1 text-emerald-500" />}</span>
                  <Select value={castRoles[m.id] || "skip"} onValueChange={(v) => setRole(m.id, v)}>
                    <SelectTrigger className="w-28 h-8 text-xs" data-testid={`cast-role-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip">— skip —</SelectItem>
                      <SelectItem value="player">Hero</SelectItem>
                      <SelectItem value="enemy">Enemy</SelectItem>
                      <SelectItem value="boss">Boss</SelectItem>
                      <SelectItem value="npc">NPC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <Button onClick={doApplyCast} disabled={applyingCast || !castGameId} className="w-full rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" data-testid="cast-apply-btn">
              {applyingCast ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />} Add cast to game
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmVariants} onOpenChange={setConfirmVariants}>
        <AlertDialogContent data-testid="meshy-variants-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Generate {versions} versions?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll sculpt {versions} different 3D takes so you can pick your favourite. Each version uses your 3D credits (about {versions}× a single model).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="meshy-variants-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="meshy-variants-confirm-btn" onClick={() => generateText()}>
              Generate {versions} versions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
